/**
 * @fileoverview Properties panel for template canvas selections.
 * @module form-builder
 *
 * Responsibilities:
 * - Render editable properties for selected forms, sections, and fields.
 * - Provide form-level category, display, behavior, and module settings.
 * - Surface field-level rule, data binding, and violation configuration.
 *
 * Dependencies:
 * - Template type contracts
 * - Local-first violation picker from @firesquirrel/local-db
 * - Data-bound widget registry
 *
 * Architecture:
 * - UI component receives host callbacks for all writes.
 * - Does not call API endpoints directly.
 * - Violation search reads through injected local-first Dexie context when
 *   available.
 *
 * Notes:
 * - Panel spacing and labels must remain readable at narrow resizable widths.
 */
import { useState, useEffect, useRef } from 'react';
import { useContactRoles } from '../../hooks/useContactRoles';
import {
  X, Plus, Pencil, ChevronDown, ChevronRight,
  ToggleLeft, Type, AlignLeft, Hash, Ruler, Calendar, CalendarClock,
  Image, PenTool, MapPin, LayoutDashboard, FileText, Repeat, Link as LinkIcon,
  Camera, CheckSquare, Circle, Trash2, Settings, Columns, Eye, EyeOff,
  AlertCircle, Check, Pin, PinOff, Users, AlertTriangle, Cpu, Info, Database, Search,
  ShieldCheck,
} from 'lucide-react';
import { DATA_BOUND_WIDGET_REGISTRY, TEMPLATE_TYPE_TO_ENTITY_CATEGORIES } from '../../data/dataBoundWidgetRegistry';
import type {
  FormField, FieldType, LayoutSection, ViolationMapping, TemplateBuilderForm, FormKind,
  ContactsModuleConfig, ViolationsModuleConfig, ModuleConfig, ContactRoleConfig,
  DataBoundDataSource, DataBoundWidgetDefinition, TemplateType,
} from '../../types/templateTypes';
import type { ViolationCodeSet } from '../../types/violationCodeSet';
import { noopViolationCodeSetService as violationService } from '../../services/violationCodeSetService';
import { ViolationPicker, type ViolationAsyncRankFn } from '@firesquirrel/local-db';
import type { Rule } from '../../types/templateTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SelectionLevel = 'field' | 'section' | 'form';

export interface FieldSelection {
  level: 'field';
  fieldId: string;
}

export interface SectionSelection {
  level: 'section';
  sectionId: string;
}

export interface FormSelection {
  level: 'form';
}

export type PanelSelection = FieldSelection | SectionSelection | FormSelection | null;

/**
 * @contract PropertiesPanelProps
 *
 * Purpose:
 * - Defines selected canvas data and mutation callbacks for the properties
 *   panel.
 *
 * Fields:
 * - selection: Currently selected form, section, field, or null.
 * - field: Selected field when selection targets a field.
 * - section: Selected section when selection targets a section.
 * - form: Selected form when selection targets a form.
 * - sections: Sections for the selected form.
 * - allFields: Fields for the selected form.
 * - rules: Current template rules used by visibility editors.
 * - onUpdateField: Applies field property updates.
 * - onUpdateSection: Applies section property updates.
 * - onUpdateForm: Applies form property updates.
 * - onDeleteField: Removes a field from the active form.
 * - onAddRule: Adds a visibility/behavior rule.
 * - onUpdateRule: Updates an existing rule.
 * - onRemoveRule: Removes an existing rule.
 * - onClose: Closes the containing panel.
 * - pinned: Legacy pin-state compatibility flag.
 * - onPin: Legacy pin callback.
 * - templateName: Current template display name.
 * - templateType: Current template type label.
 * - templateStatus: Current template status.
 * - panelWidth: Optional externally controlled width.
 * - categoryPool: Existing categories used for searchable suggestions.
 * - dexieDb: Optional local-first Dexie database for violation picking.
 * - asyncViolationRanker: Optional semantic ranker for violation search.
 *
 * Rules:
 * - UI must write through provided callbacks rather than direct API calls.
 *
 * Sync:
 * - Host local-first services own persistence and synchronization.
 */
export interface PropertiesPanelProps {
  selection: PanelSelection;
  field?: FormField | null;
  section?: LayoutSection | null;
  form?: TemplateBuilderForm | null;
  sections: LayoutSection[];
  allFields: FormField[];
  rules: Rule[];
  onUpdateField: (id: string, updates: Partial<FormField>) => void;
  onUpdateSection: (id: string, updates: Partial<LayoutSection>) => void;
  onUpdateForm?: (updates: Partial<TemplateBuilderForm>) => void;
  onDeleteField: (id: string) => void;
  onAddRule: (rule: Rule) => void;
  onUpdateRule: (rule: Rule) => void;
  onRemoveRule: (ruleId: string) => void;
  onClose: () => void;
  pinned?: boolean;
  onPin?: () => void;
  templateName?: string;
  templateType?: string;
  templateStatus?: string;
  panelWidth?: number;
  categoryPool?: string[];
  /**
   * Optional Dexie handle for the consuming app (system-web =
   * `getSystemDexieRaw()`, tenant-web = `getDexieRaw()`). When provided,
   * the violation-mapping section uses the shared local-first
   * `<ViolationPicker />`. When absent, picker UI is hidden (the
   * legacy noop service returns no data and would render nothing useful).
   */
  dexieDb?: any;
  /**
   * Optional Phase 5 AI smart-search ranker. When provided, the picker
   * surfaces the AI toggle inside its search bar and uses this ranker for
   * semantic re-ordering. When omitted, the picker stays purely lexical.
   *
   * tenant-web passes `createTenantViolationAiRanker()`. system-web does NOT
   * pass anything (no AI at the system tier — system-web only catalogs
   * cached vendor books, never renders or ranks individual violations).
   * See plan §A.12 + Review Finding 4 (Step B.2 gap-closure 2026-05-01).
   */
  asyncViolationRanker?: ViolationAsyncRankFn;
}

// ---------------------------------------------------------------------------
// FormPropertiesPanel
// ---------------------------------------------------------------------------

const FORM_KIND_OPTIONS: { value: FormKind; label: string; description: string }[] = [
  { value: 'standard', label: 'Standard', description: 'Regular inspection form with fields and sections' },
  { value: 'header', label: 'Header / Instructions', description: 'Intro page shown before the inspection begins' },
];

function CategoryCombobox({ value, pool, onChange }: {
  value: string;
  pool: string[];
  onChange: (val: string) => void;
}) {
  const [inputVal, setInputVal] = useState(value);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setInputVal(value); }, [value]);

  const filtered = pool
    .filter((c) => c.toLowerCase().includes(inputVal.toLowerCase()) && c !== inputVal)
    .slice(0, 8);

  const commit = (val: string) => {
    const trimmed = val.trim();
    onChange(trimmed);
    setInputVal(trimmed);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Search size={12} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type="text"
        value={inputVal}
        placeholder="e.g. Interior, Electrical…"
        aria-label="Category search or free text"
        className="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300"
        onChange={(e) => { setInputVal(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(inputVal); }
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      <ChevronDown size={12} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg text-xs overflow-hidden">
          {filtered.map((c) => (
            <li
              key={c}
              onMouseDown={(e) => { e.preventDefault(); commit(c); }}
              className="px-2.5 py-1.5 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
            >
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModuleConfigPanel — inline editor for ContactsModuleConfig / ViolationsModuleConfig
// ---------------------------------------------------------------------------

function ContactsConfigPanel({ config, onUpdate }: { config: ContactsModuleConfig; onUpdate: (c: ContactsModuleConfig) => void }) {
  const availableRoles = useContactRoles();
  const updateRole = (idx: number, patch: Partial<ContactRoleConfig>) => {
    const roles = config.roles.map((r, i) => i === idx ? { ...r, ...patch } : r);
    onUpdate({ ...config, roles });
  };
  const addRole = () => onUpdate({ ...config, roles: [...config.roles, { role: '', minRequired: 0, allowMultiple: false }] });
  const removeRole = (idx: number) => onUpdate({ ...config, roles: config.roles.filter((_, i) => i !== idx) });

  return (
    <div className="flex flex-col gap-4">
      {/* Roles */}
      <AccordionGroup title="Required Roles" defaultOpen>
        <div className="flex flex-col gap-2">
          <p className="text-[9px] text-gray-400 -mt-1 mb-1">Define roles inspectors must fill. Set min = 0 for optional roles.</p>
          {config.roles.map((r, idx) => (
            <div key={idx} className="p-2 rounded-lg border border-gray-100 bg-gray-50 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <select
                  value={r.role}
                  onChange={(e) => updateRole(idx, { role: e.target.value })}
                  className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300 bg-white"
                >
                  <option value="">-- Select role --</option>
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                <button onClick={() => removeRole(idx)} className="p-1 text-gray-300 hover:text-red-400 transition-colors" title="Remove role">
                  <Trash2 size={11} />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <label className="text-[9px] text-gray-500 font-medium">Min required</label>
                  <input
                    type="number" min={0} max={10}
                    value={r.minRequired}
                    title="Minimum required"
                    onChange={(e) => updateRole(idx, { minRequired: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="w-10 px-1.5 py-0.5 text-xs border border-gray-200 rounded-md text-center focus:outline-hidden focus:ring-1 focus:ring-blue-300"
                  />
                </div>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={r.allowMultiple}
                    onChange={(e) => updateRole(idx, { allowMultiple: e.target.checked })}
                    className="rounded-sm"
                  />
                  <span className="text-[9px] text-gray-500">Allow multiple</span>
                </label>
              </div>
            </div>
          ))}
          <button
            onClick={addRole}
            className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700 font-medium mt-1"
          >
            <Plus size={10} /> Add role
          </button>
        </div>
      </AccordionGroup>

      {/* Contact entry options */}
      <AccordionGroup title="Inspector Permissions" defaultOpen>
        <div className="flex flex-col gap-2">
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-xs text-gray-700">Can add, edit &amp; delete contacts</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.allowAddNew}
              onChange={(e) => onUpdate({ ...config, allowAddNew: e.target.checked })}
            />
          </label>
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-xs text-gray-700">Can select from existing contacts</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.allowSelectExisting}
              onChange={(e) => onUpdate({ ...config, allowSelectExisting: e.target.checked })}
            />
          </label>
        </div>
      </AccordionGroup>
    </div>
  );
}

function ViolationsConfigPanel({ config, onUpdate }: { config: ViolationsModuleConfig; onUpdate: (c: ViolationsModuleConfig) => void }) {
  const [codeSets, setCodeSets] = useState<ViolationCodeSet[]>([]);
  useEffect(() => { violationService.listCodeSets().then(setCodeSets); }, []);

  const toggleCodeSet = (id: string) => {
    const ids = config.codeSetIds.includes(id)
      ? config.codeSetIds.filter((x) => x !== id)
      : [...config.codeSetIds, id];
    onUpdate({ ...config, codeSetIds: ids });
  };

  return (
    <div className="flex flex-col gap-4">
      <AccordionGroup title="Code Sets" defaultOpen>
        <div className="flex flex-col gap-1.5">
          <p className="text-[9px] text-gray-400 -mt-1 mb-1">Inspector can search violations from these code sets.</p>
          {codeSets.length === 0 && (
            <p className="text-[10px] text-gray-400 italic">No active code sets found.</p>
          )}
          {codeSets.map((cs) => (
            <label key={cs._id} className="flex items-center justify-between gap-2 cursor-pointer p-1.5 rounded-lg hover:bg-gray-50">
              <div className="min-w-0">
                <div className="text-xs font-medium text-gray-700 truncate">{cs.name}</div>
                {cs.description && <div className="text-[9px] text-gray-400 truncate">{cs.description}</div>}
              </div>
              <input type="checkbox" className="rounded-sm shrink-0"
                checked={config.codeSetIds.includes(cs._id)}
                onChange={() => toggleCodeSet(cs._id)}
              />
            </label>
          ))}
        </div>
      </AccordionGroup>

      <AccordionGroup title="Search Options" defaultOpen>
        <div className="flex flex-col gap-2">
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <div>
              <div className="text-xs text-gray-700">AI-powered search</div>
              <div className="text-[9px] text-gray-400">Semantic matching across code sets</div>
            </div>
            <input type="checkbox" className="rounded-sm"
              checked={config.allowAiSearch}
              onChange={(e) => onUpdate({ ...config, allowAiSearch: e.target.checked })}
            />
          </label>
        </div>
      </AccordionGroup>

      <AccordionGroup title="Per-Violation Requirements" defaultOpen={false}>
        <div className="flex flex-col gap-2">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Notes</label>
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-xs text-gray-700">Show notes field</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.showNotes}
              onChange={(e) => onUpdate({ ...config, showNotes: e.target.checked, requireNotes: e.target.checked ? config.requireNotes : false })}
            />
          </label>
          <label className={`flex items-center justify-between gap-2 cursor-pointer ${!config.showNotes ? 'opacity-40 pointer-events-none' : ''}`}>
            <span className="text-xs text-gray-700">Require note</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.requireNotes}
              disabled={!config.showNotes}
              onChange={(e) => onUpdate({ ...config, requireNotes: e.target.checked })}
            />
          </label>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mt-1">Remedy</label>
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-xs text-gray-700">Show remedy note step</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.showRemedy}
              onChange={(e) => onUpdate({ ...config, showRemedy: e.target.checked })}
            />
          </label>
          <div className="text-[10px] text-gray-400 -mt-1">Separate field for how to fix the violation</div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mt-1">Severity</label>
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-xs text-gray-700">Show severity picker</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.showSeverity}
              onChange={(e) => onUpdate({ ...config, showSeverity: e.target.checked })}
            />
          </label>
          <div className="text-[10px] text-gray-400 -mt-1">Inspector assesses Normal / High / Critical</div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mt-1">Photos</label>
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-xs text-gray-700">Show photo attachment</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.showPhotos}
              onChange={(e) => onUpdate({ ...config, showPhotos: e.target.checked, requirePhotos: e.target.checked ? config.requirePhotos : false })}
            />
          </label>
          <label className={`flex items-center justify-between gap-2 cursor-pointer ${!config.showPhotos ? 'opacity-40 pointer-events-none' : ''}`}>
            <span className="text-xs text-gray-700">Require photo</span>
            <input type="checkbox" className="rounded-sm"
              checked={config.requirePhotos}
              disabled={!config.showPhotos}
              onChange={(e) => onUpdate({ ...config, requirePhotos: e.target.checked })}
            />
          </label>
        </div>
      </AccordionGroup>
    </div>
  );
}

function ModuleConfigPanel({ form, onUpdate }: { form: TemplateBuilderForm; onUpdate: (updates: Partial<TemplateBuilderForm>) => void }) {
  const mc = form.moduleConfig;
  const isContacts = mc?.moduleType === 'contacts';
  const isViolations = mc?.moduleType === 'violations';

  const handleModuleUpdate = (updated: ModuleConfig) => onUpdate({ moduleConfig: updated });

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Module identity header */}
      <div className={`px-3 py-2.5 border-b flex items-center gap-2 ${
        isContacts ? 'bg-blue-50 border-blue-100' : 'bg-orange-50 border-orange-100'
      }`}>
        {isContacts
          ? <Users size={13} className="text-blue-600 shrink-0" />
          : <AlertTriangle size={13} className="text-orange-600 shrink-0" />}
        <div>
          <div className="text-xs font-semibold text-gray-800">{isContacts ? 'Contacts Module' : 'Additional Violations Module'}</div>
          <div className="text-[9px] text-gray-400">{isContacts
            ? 'Configure required roles and inspector permissions'
            : 'Configure code sets and per-violation requirements'}
          </div>
        </div>
      </div>

      {/* Name field — always editable */}
      <AccordionGroup title="Identity" defaultOpen>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Form Name</label>
          <input
            type="text"
            value={form.name}
            title="Form name"
            placeholder="Form name…"
            onChange={(e) => onUpdate({ name: e.target.value })}
            className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300"
          />
        </div>
      </AccordionGroup>

      {/* Module-specific config */}
      <div className="px-3 py-2">
        {isContacts && mc && (
          <ContactsConfigPanel
            config={mc as ContactsModuleConfig}
            onUpdate={(updated) => handleModuleUpdate(updated)}
          />
        )}
        {isViolations && mc && (
          <ViolationsConfigPanel
            config={mc as ViolationsModuleConfig}
            onUpdate={(updated) => handleModuleUpdate(updated)}
          />
        )}
        {!mc && (
          <p className="text-[10px] text-gray-400 italic">No module configuration found.</p>
        )}
      </div>

      {/* Version footer */}
      <div className="px-3 py-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <Cpu size={9} />
          <span>System Module · v{form.version}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeaderFormPropertiesPanel — shown when formKind === 'header'
// ---------------------------------------------------------------------------

function HeaderFormPropertiesPanel({ form, onUpdate }: { form: TemplateBuilderForm; onUpdate: (updates: Partial<TemplateBuilderForm>) => void }) {
  const contentLength = form.description ? form.description.replace(/<[^>]*>/g, '').trim().length : 0;
  const hasContent = contentLength > 0;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header identity band */}
      <div className="px-3 py-2.5 border-b flex items-center gap-2 bg-amber-50 border-amber-100">
        <FileText size={13} className="text-amber-600 shrink-0" />
        <div>
          <div className="text-xs font-semibold text-gray-800">Header / Instructions Form</div>
          <div className="text-[9px] text-gray-500 mt-0.5">Shown as the first page before the inspection begins</div>
        </div>
      </div>

      <AccordionGroup title="Identity" defaultOpen>
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Form Name</label>
            <input
              type="text"
              value={form.name}
              title="Form name"
              placeholder="e.g. Instructions"
              onChange={(e) => onUpdate({ name: e.target.value })}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-amber-300"
            />
          </div>
        </div>
      </AccordionGroup>

      <AccordionGroup title="Content Status" defaultOpen>
        <div className="flex flex-col gap-2">
          <div className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border ${hasContent ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
            {hasContent ? (
              <Check size={12} className="text-green-600 shrink-0" />
            ) : (
              <AlertCircle size={12} className="text-amber-500 shrink-0" />
            )}
            <div>
              <div className={`text-[10px] font-medium ${hasContent ? 'text-green-700' : 'text-amber-700'}`}>
                {hasContent ? 'Content added' : 'No content yet'}
              </div>
              {hasContent && (
                <div className="text-[9px] text-gray-400 mt-0.5">~{contentLength} characters</div>
              )}
            </div>
          </div>
          <p className="text-[9px] text-gray-400 leading-relaxed">
            Expand the header form card on the canvas to edit the rich-text content using the built-in editor.
          </p>
        </div>
      </AccordionGroup>

      <AccordionGroup title="Display Options" defaultOpen={false}>
        <div className="flex flex-col gap-3">
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <div>
              <div className="text-xs font-medium text-gray-700">Show page number</div>
              <div className="text-[9px] text-gray-400">Display "Page 1 of N" on this page in mobile</div>
            </div>
            <button
              type="button"
              onClick={() => onUpdate({ display: { ...form.display, showPageNumber: !form.display?.showPageNumber } })}
              className={`relative inline-flex h-4 w-7 shrink-0 rounded-full border-2 border-transparent transition-colors ${
                form.display?.showPageNumber ? 'bg-amber-500' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${
                form.display?.showPageNumber ? 'translate-x-3' : 'translate-x-0'
              }`} />
            </button>
          </label>
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <div>
              <div className="text-xs font-medium text-gray-700">Require acknowledgement</div>
              <div className="text-[9px] text-gray-400">Inspector must tap "I understand" before proceeding</div>
            </div>
            <button
              type="button"
              onClick={() => onUpdate({ display: { ...form.display, requireAck: !form.display?.requireAck } })}
              className={`relative inline-flex h-4 w-7 shrink-0 rounded-full border-2 border-transparent transition-colors ${
                form.display?.requireAck ? 'bg-amber-500' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${
                form.display?.requireAck ? 'translate-x-3' : 'translate-x-0'
              }`} />
            </button>
          </label>
        </div>
      </AccordionGroup>

      <AccordionGroup title="About Header Forms" defaultOpen={false}>
        <div className="space-y-2 text-[10px] text-gray-500 leading-relaxed">
          <p>Header forms appear as the first screen before any inspection form. They are ideal for:</p>
          <ul className="list-disc pl-3 space-y-1">
            <li>Safety briefings and inspection scope notes</li>
            <li>Step-by-step instructions for inspectors</li>
            <li>Contact information and escalation procedures</li>
            <li>Colorful, formatted guidance with images</li>
          </ul>
          <p className="text-[9px] text-gray-400">Only one header form is allowed per template. Sections and fields cannot be added — use the rich-text editor instead.</p>
        </div>
      </AccordionGroup>

      <div className="px-3 py-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <span className="px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-600 font-medium text-[9px]">Header</span>
          <span>·</span>
          <span>v{form.version}</span>
          <span>·</span>
          <span className={form.status === 'published' ? 'text-green-600 capitalize' : 'text-amber-600 capitalize'}>{form.status}</span>
        </div>
      </div>
    </div>
  );
}

function FormPropertiesPanel({ form, onUpdate, categoryPool = [] }: { form: TemplateBuilderForm; onUpdate: (updates: Partial<TemplateBuilderForm>) => void; categoryPool?: string[] }) {
  if (form.formKind === 'module') {
    return <ModuleConfigPanel form={form} onUpdate={onUpdate} />;
  }
  if (form.formKind === 'header') {
    return <HeaderFormPropertiesPanel form={form} onUpdate={onUpdate} />;
  }
  return (
    <div className="flex-1 overflow-y-auto">
      <AccordionGroup title="Identity" defaultOpen>
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Form Name</label>
            <input
              type="text"
              value={form.name}
              title="Form name"
              placeholder="Form name…"
              onChange={(e) => onUpdate({ name: e.target.value })}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
            <textarea
              value={form.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value || undefined })}
              rows={2}
              placeholder="Optional description…"
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300 resize-none"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Category</label>
            <CategoryCombobox
              value={form.category || ''}
              pool={categoryPool}
              onChange={(val) => onUpdate({ category: val || undefined })}
            />
            <p className="text-[9px] text-gray-400 mt-0.5">Type to search existing categories or create a new one</p>
          </div>
        </div>
      </AccordionGroup>

      <AccordionGroup title="Form Type" defaultOpen>
        {(() => {
          const kind = form.formKind ?? 'standard';
          const opt = FORM_KIND_OPTIONS.find((o) => o.value === kind) ?? FORM_KIND_OPTIONS[0];
          return (
            <div className="flex items-start gap-2.5 p-2 rounded-lg border border-gray-100 bg-gray-50">
              <div>
                <div className="text-xs font-medium text-gray-800">{opt.label}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{opt.description}</div>
              </div>
            </div>
          );
        })()}
      </AccordionGroup>

      <AccordionGroup title="Behavior" defaultOpen={false}>
        <div className="flex flex-col gap-3">
          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <div>
              <div className="text-xs font-medium text-gray-700">Optional form</div>
              <div className="text-[10px] text-gray-400">Inspectors can skip this form during inspection</div>
            </div>
            <button
              onClick={() => onUpdate({ isFixedForm: form.isFixedForm ? undefined : false })}
              className={`relative inline-flex h-4.5 w-8 shrink-0 rounded-full border-2 border-transparent transition-colors ${
                !form.isFixedForm ? 'bg-blue-500' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                !form.isFixedForm ? 'translate-x-3.5' : 'translate-x-0'
              }`} />
            </button>
          </label>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Initial Visibility</label>
            <select
              value={form.initialVisibility ?? 'shown'}
              title="Initial visibility"
              onChange={(e) => onUpdate({ initialVisibility: e.target.value as 'shown' | 'hidden' })}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300 bg-white"
            >
              <option value="shown">Shown by default</option>
              <option value="hidden">Hidden by default (rule-controlled)</option>
            </select>
          </div>
        </div>
      </AccordionGroup>

      <AccordionGroup title="Mobile Display" defaultOpen={false}>
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Short label</label>
            <input
              type="text"
              maxLength={6}
              value={form.display?.abbreviation || ''}
              title="Short label"
              onChange={(e) => onUpdate({ display: { ...form.display, abbreviation: e.target.value || undefined } })}
              placeholder="e.g. INT, EXT, ID"
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300"
            />
            <p className="text-[9px] text-gray-400 mt-0.5">1–6 chars shown in mobile nav bar</p>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Accent color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.display?.color || '#3b82f6'}
                title="Accent color"
                onChange={(e) => onUpdate({ display: { ...form.display, color: e.target.value } })}
                className="w-8 h-7 rounded-sm cursor-pointer border border-gray-200"
              />
              <span className="text-[10px] text-gray-500">{form.display?.color || '#3b82f6'}</span>
            </div>
          </div>
        </div>
      </AccordionGroup>

      <div className="px-3 py-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
          <span>v{form.version}</span>
          <span>·</span>
          <span className={`capitalize ${
            form.status === 'published' ? 'text-green-600' : 'text-amber-600'
          }`}>{form.status}</span>
          <span>·</span>
          <span>{form.fields.length} field{form.fields.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field icons map
// ---------------------------------------------------------------------------

const FIELD_ICONS: Record<FieldType, React.ComponentType<{ size?: number | string; className?: string }>> = {
  toggle: ToggleLeft,
  text: Type,
  textarea: AlignLeft,
  number: Hash,
  measurement: Ruler,
  dropdown: ChevronDown,
  checkboxGroup: CheckSquare,
  radioGroup: Circle,
  camera: Camera,
  date: Calendar,
  dateTime: CalendarClock,
  photo: Image,
  signature: PenTool,
  location: MapPin,
  section: LayoutDashboard,
  instructions: FileText,
  repeatableList: Repeat,
  dataBound: LinkIcon,
  inspectors: Users,
};

// ---------------------------------------------------------------------------
// Accordion group
// ---------------------------------------------------------------------------

interface AccordionGroupProps {
  title: string;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
}

function AccordionGroup({ title, defaultOpen = true, badge, children }: AccordionGroupProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {title}
          {badge}
        </div>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && <div className="px-4 pb-3 space-y-3">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Completion badge
// ---------------------------------------------------------------------------

function CompletionBadge({ complete, label }: { complete: boolean; label?: string }) {
  if (complete) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-green-100 text-green-700">
        <Check size={8} /> {label || 'Done'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-amber-100 text-amber-700">
      <AlertCircle size={8} /> {label || 'Incomplete'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// VisibilityRuleAdder (moved from FormBuilderPage)
// ---------------------------------------------------------------------------

type RuleAction = 'hideSection' | 'showSection' | 'hideField' | 'showField';

function VisibilityRuleAdder({
  field, sections, allFields, onAdd, editingRule, onUpdate, onCancelEdit,
}: {
  field: FormField;
  sections: LayoutSection[];
  allFields: FormField[];
  onAdd: (rule: Rule) => void;
  editingRule: Rule | null;
  onUpdate?: (rule: Rule) => void;
  onCancelEdit?: () => void;
}) {
  const editAct = editingRule?.then[0] as { action?: string; sectionId?: string; fieldId?: string } | undefined;
  const editCond = editingRule?.when as { op?: string; value?: any } | undefined;
  const editAction = (editAct?.action || 'hideSection') as RuleAction;
  const editTargetId = editAct?.fieldId || editAct?.sectionId || '';
  const isNumericField = field.type === 'number' || field.type === 'measurement';

  const [open, setOpen] = useState(!!editingRule);
  const [action, setAction] = useState<RuleAction>(editAction);
  const [targetId, setTargetId] = useState(editTargetId);
  const [triggerValue, setTriggerValue] = useState<any>(editCond?.value ?? (field.type === 'toggle' ? false : ''));
  const [triggerOp, setTriggerOp] = useState<string>(editCond?.op || (isNumericField ? 'gt' : 'equals'));

  useEffect(() => {
    if (editingRule) {
      const a = editingRule.then[0] as { action?: string; sectionId?: string; fieldId?: string } | undefined;
      const c = editingRule.when as { op?: string; value?: any } | undefined;
      setAction((a?.action || 'hideSection') as RuleAction);
      setTargetId(a?.fieldId || a?.sectionId || '');
      setTriggerValue(c?.value ?? (field.type === 'toggle' ? false : ''));
      setTriggerOp(c?.op || (isNumericField ? 'gt' : 'equals'));
      setOpen(true);
    }
  }, [editingRule, field.type, isNumericField]);

  const isFieldAction = action === 'showField' || action === 'hideField';
  const targetFields = allFields.filter((f) => f.id !== field.id && f.type !== 'section' && f.type !== 'instructions');

  if (!open && !editingRule) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-1 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
      >
        <Plus size={10} /> Add Rule
      </button>
    );
  }

  const handleSave = () => {
    if (!targetId) return;
    const actionLabel = action === 'showSection' ? 'Show section' : action === 'hideSection' ? 'Hide section' : action === 'showField' ? 'Show field' : 'Hide field';
    const rule: Rule = {
      id: editingRule?.id || `rule-${Date.now()}`,
      name: `${field.label} → ${actionLabel}`,
      scope: 'form',
      when: { field: field.id, op: (isNumericField ? triggerOp : 'equals') as any, value: triggerValue },
      then: [isFieldAction ? { action, fieldId: targetId } as any : { action, sectionId: targetId } as any],
    };
    if (editingRule && onUpdate) {
      onUpdate(rule);
    } else {
      onAdd(rule);
    }
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setTargetId('');
    if (editingRule && onCancelEdit) onCancelEdit();
  };

  const options = field.extras?.options as { value: string; label: string }[] | undefined;

  return (
    <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded-sm space-y-2">
      <div className="text-[10px] font-medium text-blue-700">{editingRule ? 'Edit Visibility Rule' : 'New Visibility Rule'}</div>
      <div>
        <label className="block text-[10px] text-gray-500 mb-0.5">When value is</label>
        {field.type === 'toggle' ? (
          <select value={String(triggerValue)} title="Trigger value" onChange={(e) => setTriggerValue(e.target.value === 'true')} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm">
            <option value="true">Yes (On)</option>
            <option value="false">No (Off)</option>
          </select>
        ) : isNumericField ? (
          <div className="flex gap-1">
            <select value={triggerOp} title="Comparison operator" onChange={(e) => setTriggerOp(e.target.value)} className="w-24 px-1 py-1 text-xs border border-gray-300 rounded-sm">
              <option value="gt">Greater than</option>
              <option value="gte">≥</option>
              <option value="lt">Less than</option>
              <option value="lte">≤</option>
              <option value="equals">Equals</option>
            </select>
            <input
              type="number"
              value={triggerValue ?? ''}
              onChange={(e) => setTriggerValue(e.target.value !== '' ? Number(e.target.value) : '')}
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-sm"
              placeholder={field.extras?.unit ? `e.g. 100 ${field.extras.unit}` : 'e.g. 100'}
            />
          </div>
        ) : options ? (
          <select value={String(triggerValue)} title="Trigger value" onChange={(e) => setTriggerValue(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm">
            <option value="">Select…</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input type="text" value={triggerValue || ''} onChange={(e) => setTriggerValue(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" placeholder="Value…" />
        )}
      </div>
      <div>
        <label className="block text-[10px] text-gray-500 mb-0.5">Then</label>
        <select value={action} title="Rule action" onChange={(e) => { setAction(e.target.value as RuleAction); setTargetId(''); }} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm">
          <option value="hideSection">Hide Section</option>
          <option value="showSection">Show Section</option>
          <option value="hideField">Hide Field</option>
          <option value="showField">Show Field</option>
        </select>
      </div>
      <div>
        <label className="block text-[10px] text-gray-500 mb-0.5">{isFieldAction ? 'Field' : 'Section'}</label>
        <select value={targetId} title={isFieldAction ? 'Target field' : 'Target section'} onChange={(e) => setTargetId(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm">
          <option value="">{isFieldAction ? 'Select field…' : 'Select section…'}</option>
          {isFieldAction
            ? targetFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)
            : sections.map((s) => <option key={s.id} value={s.id}>{s.title || `Section ${s.order + 1}`}</option>)
          }
        </select>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={handleSave} disabled={!targetId} className="px-2 py-1 text-[10px] font-medium text-white rounded-sm disabled:opacity-50 bg-(--brand-primary)">
          {editingRule ? 'Save' : 'Add'}
        </button>
        <button onClick={handleClose} className="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// VisibilityRulesSection
// ---------------------------------------------------------------------------

function VisibilityRulesSection({ field, sections, allFields, rules, onAddRule, onUpdateRule, onRemoveRule }: {
  field: FormField;
  sections: LayoutSection[];
  allFields: FormField[];
  rules: Rule[];
  onAddRule: (rule: Rule) => void;
  onUpdateRule: (rule: Rule) => void;
  onRemoveRule: (ruleId: string) => void;
}) {
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const fieldRules = rules.filter((r) => {
    const cond = r.when as { field?: string };
    return cond.field === field.id;
  });
  const editingRule = editingRuleId ? fieldRules.find((r) => r.id === editingRuleId) || null : null;
  const OP_SYMBOLS: Record<string, string> = { equals: '=', gt: '>', gte: '≥', lt: '<', lte: '≤' };

  return (
    <div>
      <p className="text-[10px] text-gray-400 mb-2">Show or hide sections and fields based on this field's value.</p>
      {fieldRules.map((rule) => {
        const act = rule.then[0] as { action: string; sectionId?: string; fieldId?: string };
        const isFieldAct = act.action === 'showField' || act.action === 'hideField';
        const targetLabel = isFieldAct
          ? (allFields.find((f) => f.id === act.fieldId)?.label || act.fieldId)
          : (sections.find((s) => s.id === act.sectionId)?.title || act.sectionId);
        const actionLabel = act.action === 'showSection' ? 'Show section' : act.action === 'hideSection' ? 'Hide section' : act.action === 'showField' ? 'Show field' : 'Hide field';
        const cond = rule.when as { field?: string; op?: string; value?: any };
        const opSymbol = OP_SYMBOLS[cond.op || 'equals'] || cond.op || '=';
        const valueLabel = field.type === 'toggle'
          ? (cond.value === true ? 'Yes' : 'No')
          : ((field.type === 'number' || field.type === 'measurement') ? `${opSymbol} ${cond.value}` : String(cond.value));
        const isEditing = editingRuleId === rule.id;
        return (
          <div
            key={rule.id}
            onClick={() => { if (!isEditing) setEditingRuleId(rule.id); }}
            className={`flex items-center gap-1 text-xs px-2 py-1.5 rounded-sm border mb-1 cursor-pointer transition-colors ${isEditing ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'}`}
          >
            <Pencil size={9} className="text-blue-400 shrink-0" />
            <span className="flex-1 truncate">When <strong>{valueLabel}</strong> → {actionLabel} "{targetLabel}"</span>
            <button onClick={(e) => { e.stopPropagation(); if (editingRuleId === rule.id) setEditingRuleId(null); onRemoveRule(rule.id); }} title="Remove rule" className="p-0.5 text-blue-400 hover:text-red-500 shrink-0">
              <X size={10} />
            </button>
          </div>
        );
      })}
      {(sections.length > 0 || allFields.length > 1) && (
        <VisibilityRuleAdder
          key={editingRuleId || 'new'}
          field={field}
          sections={sections}
          allFields={allFields}
          onAdd={onAddRule}
          editingRule={editingRule}
          onUpdate={(r) => { onUpdateRule(r); setEditingRuleId(null); }}
          onCancelEdit={() => setEditingRuleId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ViolationConfigSection
// ---------------------------------------------------------------------------

/**
 * @component ViolationOccurrencesEditor
 *
 * Purpose:
 * - Shared occurrence toggle for a `ViolationMapping`. Failed-state capture
 *   requirements are configured outside the violation selector.
 *
 * Props:
 * - vm: Current mapping.
 * - onUpdate: Partial patcher for the mapping.
 *
 * Data Source:
 * - Stateless.
 */
function ViolationOccurrencesEditor({ vm, onUpdate }: {
  vm: ViolationMapping;
  onUpdate: (patch: Partial<ViolationMapping>) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={vm.allowMultipleInstances} onChange={(e) => onUpdate({ allowMultipleInstances: e.target.checked })} className="rounded-sm border-gray-300" />
        <span className="text-xs text-gray-700">Allow Multiple Occurrences</span>
      </label>
    </div>
  );
}

function ViolationConfigSection({ field, onUpdate, dexieDb, asyncViolationRanker }: {
  field: FormField;
  onUpdate: (id: string, updates: Partial<FormField>) => void;
  dexieDb?: any;
  asyncViolationRanker?: ViolationAsyncRankFn;
}) {
  const [violationPickerOpen, setViolationPickerOpen] = useState(false);
  const [violationDetailsOpen, setViolationDetailsOpen] = useState(false);

  const extras = field.extras || {};
  const hasPassFail = extras.hasPassFail || false;
  const passFailScoring = extras.passFailScoring || false;
  const failsOn = extras.failsOn ?? false;
  const failValues: string[] = extras.failValues || [];
  const hasViolation = !!(field.violationMappings && field.violationMappings.length > 0);
  const vm = hasViolation ? field.violationMappings![0] : null;
  const isToggle = field.type === 'toggle';
  const hasOptions = field.type === 'dropdown' || field.type === 'checkboxGroup' || field.type === 'radioGroup';
  const options: { value: string; label: string }[] = hasOptions ? (extras.options || []) : [];

  const updateExtras = (patch: Record<string, any>) => onUpdate(field.id, { extras: { ...extras, ...patch } });
  const setViolationMapping = (mapping: ViolationMapping | null) => onUpdate(field.id, { violationMappings: mapping ? [mapping] : [] });
  const buildTrigger = (v: any) => ({ field: field.id, op: 'equals' as const, value: v });

  const togglePassFail = (enabled: boolean) => {
    if (!enabled) {
      setViolationMapping(null);
      updateExtras({
        hasPassFail: false,
        passFailScoring: false,
        failsOn: undefined,
        failValues: undefined,
        showNotesAlways: undefined,
        showCameraAlways: undefined,
      });
      return;
    }
    // Always persist failsOn so the renderer can default correctly without
    // relying on a ?? false fallback. Keep any existing value so re-enabling
    // after a disable round-trip doesn't clobber a previously chosen setting.
    updateExtras({ hasPassFail: true, failsOn: extras.failsOn ?? false });
  };

  const togglePassFailScoring = (enabled: boolean) => {
    updateExtras({ passFailScoring: enabled });
  };

  const handleFailsOnChange = (value: boolean) => {
    updateExtras({ failsOn: value });
    if (vm) setViolationMapping({ ...vm, trigger: buildTrigger(value) });
  };

  const handleFailValuesChange = (value: string, checked: boolean) => {
    const next = checked ? [...failValues, value] : failValues.filter((v) => v !== value);
    updateExtras({ failValues: next });
    if (vm && next.length > 0) setViolationMapping({ ...vm, trigger: { field: field.id, op: 'equals', value: next[0] } });
  };

  // Three-state violation selector (Step B.2 gap-closure 2026-05-01):
  //   'none'     — no mapping exists on this field.
  //   'pending'  — bindingMode: 'pending-tenant-bind' (empty codeSetId/code;
  //                 tenant admin resolves during import walkthrough).
  //   'bound'    — real mapping with codeSetId + code.
  //
  // System-web (no `dexieDb` prop) only exposes 'none' and 'pending' because
  // the system tier never reads or ranks individual violations (plan §A.12
  // and Review Finding 4). Tenant-web exposes all three.
  type ViolationSelectorMode = 'none' | 'pending' | 'bound';
  const currentMode: ViolationSelectorMode = !hasViolation
    ? 'none'
    : vm?.bindingMode === 'pending-tenant-bind'
      ? 'pending'
      : 'bound';

  const baseTrigger = () => isToggle
    ? buildTrigger(failsOn)
    : { field: field.id, op: 'equals' as const, value: failValues[0] || '' };

  const setMode = (next: ViolationSelectorMode) => {
    const requireNotes = extras.requireNotesOnFail ?? vm?.requireNotes ?? false;
    const requirePhotos = extras.requirePhotosOnFail ?? vm?.requirePhotos ?? false;
    const requireLocation = extras.requireLocationOnFail ?? vm?.requireLocation ?? false;
    if (next === 'none') {
      setViolationMapping(null);
      setViolationPickerOpen(false);
      return;
    }
    if (next === 'pending') {
      setViolationMapping({
        codeSetId: '',
        code: '',
        trigger: baseTrigger(),
        requireNotes,
        requirePhotos,
        requireLocation,
        allowMultipleInstances: false,
        bindingMode: 'pending-tenant-bind',
        bindingHint: vm?.bindingHint,
      });
      setViolationPickerOpen(false);
      return;
    }
    // 'bound'
    setViolationMapping({
      codeSetId: vm?.bindingMode === 'bound' ? vm?.codeSetId || '' : '',
      code: vm?.code || '',
      title: vm?.title,
      contentSnippet: vm?.contentSnippet,
      codeReference: vm?.codeReference,
      trigger: vm?.trigger ?? baseTrigger(),
      requireNotes,
      requirePhotos,
      requireLocation,
      allowMultipleInstances: vm?.allowMultipleInstances ?? false,
      bindingMode: 'bound',
    });
    if (dexieDb) setViolationPickerOpen(true);
  };

  const updateVM = (patch: Partial<ViolationMapping>) => { if (vm) setViolationMapping({ ...vm, ...patch }); };
  const selectedViolationDescription = vm?.title || 'Description unavailable';
  const selectedViolationText = vm?.contentSnippet;

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={hasPassFail} onChange={(e) => togglePassFail(e.target.checked)} className="rounded-sm border-gray-300" />
        <span className="text-sm text-gray-700">This field has a pass/fail state</span>
      </label>

      {hasPassFail && (
        <div className="ml-4 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={passFailScoring} onChange={(e) => togglePassFailScoring(e.target.checked)} className="rounded-sm border-gray-300" />
            <span className="text-sm text-gray-700 font-medium">Pass / Fail scoring</span>
          </label>
          {isToggle && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fails when</label>
              <select value={failsOn ? 'true' : 'false'} title="Fails when" onChange={(e) => handleFailsOnChange(e.target.value === 'true')} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)">
                <option value="false">No (Off)</option>
                <option value="true">Yes (On)</option>
              </select>
            </div>
          )}
          {hasOptions && options.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fails when value is</label>
              <div className="space-y-1 max-h-28 overflow-y-auto">
                {options.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={failValues.includes(opt.value)} onChange={(e) => handleFailValuesChange(opt.value, e.target.checked)} className="rounded-sm border-gray-300" />
                    <span className="text-xs text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
              {/* Three-state violation selector (Step B.2 gap-closure). */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Violation on failure</label>
                <select
                  value={currentMode}
                  onChange={(e) => setMode(e.target.value as ViolationSelectorMode)}
                  title="Violation on failure"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
                >
                  <option value="none">None</option>
                  <option value="pending">Violation to be added later</option>
                  {dexieDb ? <option value="bound">Pick a violation now</option> : null}
                </select>
                {!dexieDb ? (
                  <p className="text-[10px] text-gray-400 italic mt-1">
                    System-web template authoring: tenants pick the concrete code on import via the Bind Violations walkthrough.
                  </p>
                ) : null}
              </div>

              {currentMode === 'pending' && vm ? (
                <div className="ml-4 space-y-2 border-l-2 border-amber-200 pl-3">
                  <div className="rounded-sm bg-amber-50 border border-amber-200 px-2 py-1.5 text-[11px] text-amber-800">
                    Marked as <strong>pending</strong>. On import, tenants must bind this to a concrete code in their active code sets before they can publish the template.
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Hint for tenant admin (optional)</label>
                    <textarea
                      value={vm.bindingHint ?? ''}
                      onChange={(e) => updateVM({ bindingHint: e.target.value })}
                      placeholder="e.g. Use your local amendment for Section 903.2.1 if adopted; otherwise IFC 903.2.1."
                      rows={3}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
                    />
                  </div>
                  <ViolationOccurrencesEditor vm={vm} onUpdate={updateVM} />
                </div>
              ) : null}

              {currentMode === 'bound' && vm && dexieDb ? (
                <div className="ml-4 space-y-2.5 border-l-2 border-red-200 pl-3">
                  {/* Selected violation summary. */}
                  <div>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Violation</label>
                    {vm.code && vm.codeSetId ? (
                      <div className="flex items-start gap-2 px-2 py-1.5 border border-gray-200 rounded-sm bg-white">
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-mono font-bold text-red-600">
                            {vm.code}
                          </div>
                          <div className="text-[11px] font-medium text-gray-800 truncate">
                            {selectedViolationDescription}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setViolationDetailsOpen(true)}
                          title="Show violation"
                          className="p-0.5 text-gray-400 hover:text-red-500"
                        >
                          <Eye size={10} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setViolationPickerOpen(true)}
                          className="text-[11px] font-medium text-gray-500 hover:text-red-600 underline-offset-2 hover:underline"
                        >
                          Select
                        </button>
                        <button
                          type="button"
                          onClick={() => setMode('none')}
                          className="text-[11px] font-medium text-gray-500 hover:text-red-600 underline-offset-2 hover:underline"
                        >
                          Unlink
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setViolationPickerOpen(true)}
                        className="w-full rounded-sm border border-dashed border-red-200 bg-red-50/50 px-2 py-2 text-left text-[11px] font-medium text-red-700 hover:bg-red-50"
                      >
                        Open violation picker…
                      </button>
                    )}
                  </div>
                  <ViolationOccurrencesEditor vm={vm} onUpdate={updateVM} />
                </div>
              ) : null}
              {violationPickerOpen && dexieDb ? (
                <div className="fixed inset-0 z-120 flex items-center justify-center p-6">
                  <div className="absolute inset-0 bg-black/50" onClick={() => setViolationPickerOpen(false)} />
                  <div className="relative flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Pick violation on failure</h3>
                        <p className="mt-0.5 text-xs text-gray-500">Select a code set, search violations, and bind the selected code to this failed field.</p>
                      </div>
                      <button
                        type="button"
                        title="Close violation picker"
                        onClick={() => setViolationPickerOpen(false)}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto p-5">
                      <ViolationPicker
                        db={dexieDb}
                        mode="single"
                        asyncRankFn={asyncViolationRanker}
                        autoFocus
                        onPickOne={(sel) => {
                          const requireNotes = extras.requireNotesOnFail ?? vm?.requireNotes ?? false;
                          const requirePhotos = extras.requirePhotosOnFail ?? vm?.requirePhotos ?? false;
                          const requireLocation = extras.requireLocationOnFail ?? vm?.requireLocation ?? false;
                          setViolationMapping({
                            codeSetId: sel.codeSetId,
                            code: sel.code,
                            title: sel.title,
                            contentSnippet: sel.contentSnippet || sel.displayText || sel.bodyText,
                            codeReference: sel.codeReference,
                            trigger: vm?.trigger ?? baseTrigger(),
                            requireNotes,
                            requirePhotos,
                            requireLocation,
                            allowMultipleInstances: vm?.allowMultipleInstances ?? false,
                            bindingMode: 'bound',
                          });
                          setViolationPickerOpen(false);
                        }}
                        placeholder="Search the code library…"
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              {violationDetailsOpen && vm ? (
                <div className="fixed inset-0 z-121 flex items-center justify-center p-6">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setViolationDetailsOpen(false)} />
                  <div className="relative w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                      <h3 className="text-sm font-semibold text-gray-900">Violation details</h3>
                      <button
                        type="button"
                        title="Close violation details"
                        onClick={() => setViolationDetailsOpen(false)}
                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="space-y-3 p-4">
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Code</div>
                        <div className="mt-1 font-mono text-sm font-bold text-gray-900">{vm.code}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Description</div>
                        <div className="mt-1 text-sm text-gray-800">{selectedViolationDescription}</div>
                      </div>
                      {selectedViolationText ? (
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Text</div>
                          <div className="mt-1 max-h-36 overflow-y-auto rounded-sm border border-gray-200 bg-gray-50 p-2 text-xs leading-5 text-gray-700">
                            {selectedViolationText}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionPropertiesPanel — section-level editing
// ---------------------------------------------------------------------------

function SectionPropertiesPanel({ section, onUpdate }: {
  section: LayoutSection;
  onUpdate: (id: string, updates: Partial<LayoutSection>) => void;
}) {
  return (
    <div className="flex-1 overflow-y-auto">
      <AccordionGroup title="Section Settings" defaultOpen>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Section Title</label>
          <input
            type="text"
            value={section.title || ''}
            onChange={(e) => onUpdate(section.id, { title: e.target.value })}
            placeholder="e.g. General Information"
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary) focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Columns</label>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                onClick={() => onUpdate(section.id, { columns: n })}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  (section.columns || 1) === n
                    ? 'bg-(--brand-primary) text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {n === 1 ? '1 (full)' : n}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">Number of columns for fields in this section.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={section.collapsible || false}
            onChange={(e) => onUpdate(section.id, { collapsible: e.target.checked })}
            className="rounded-sm border-gray-300"
          />
          <span className="text-sm text-gray-700">Collapsible</span>
        </label>
        {section.collapsible && (
          <label className="flex items-center gap-2 cursor-pointer ml-5">
            <input
              type="checkbox"
              checked={section.defaultCollapsed || false}
              onChange={(e) => onUpdate(section.id, { defaultCollapsed: e.target.checked })}
              className="rounded-sm border-gray-300"
            />
            <span className="text-sm text-gray-700">Collapsed by default</span>
          </label>
        )}
      </AccordionGroup>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FieldPropertiesPanel — all field property groups with accordions
// ---------------------------------------------------------------------------

function FieldPropertiesPanel({ field, sections, allFields, rules, onUpdate, onRemove, onAddRule, onUpdateRule, onRemoveRule, dexieDb, asyncViolationRanker, templateType }: {
  field: FormField;
  sections: LayoutSection[];
  allFields: FormField[];
  rules: Rule[];
  onUpdate: (id: string, updates: Partial<FormField>) => void;
  onRemove: (id: string) => void;
  onAddRule: (rule: Rule) => void;
  onUpdateRule: (rule: Rule) => void;
  onRemoveRule: (ruleId: string) => void;
  dexieDb?: any;
  asyncViolationRanker?: ViolationAsyncRankFn;
  templateType?: string;
}) {
  const hasOptions = field.type === 'dropdown' || field.type === 'checkboxGroup' || field.type === 'radioGroup';
  const options: { value: string; label: string }[] = hasOptions ? (field.extras?.options || []) : [];
  const hasVisibilityRuleSupport = ['toggle', 'dropdown', 'radioGroup', 'checkboxGroup', 'number', 'measurement'].includes(field.type);
  const hasPassFailSupport = ['toggle', 'dropdown', 'checkboxGroup', 'radioGroup'].includes(field.type);

  const fieldRules = rules.filter((r) => {
    const cond = r.when as { field?: string };
    return cond.field === field.id;
  });
  const hasViolation = !!(field.violationMappings && field.violationMappings.length > 0);

  const basicComplete = !!field.label;
  const rulesComplete = !hasVisibilityRuleSupport || fieldRules.length === 0;
  const violationsComplete = !hasPassFailSupport || !field.extras?.hasPassFail || hasViolation;

  const updateOption = (idx: number, key: 'value' | 'label', val: string) => {
    const next = [...options];
    next[idx] = { ...next[idx], [key]: val };
    onUpdate(field.id, { extras: { ...field.extras, options: next } });
  };

  const updateFailureCaptureOption = (
    key: 'showNotesAlways' | 'showCameraAlways' | 'requireNotesOnFail' | 'requirePhotosOnFail' | 'requireLocationOnFail' | 'requireCorrectiveActionOnFail',
    value: boolean,
  ) => {
    const updates: Partial<FormField> = {
      extras: { ...field.extras, [key]: value },
    };
    if (
      field.violationMappings?.length
      && (key === 'requireNotesOnFail' || key === 'requirePhotosOnFail' || key === 'requireLocationOnFail' || key === 'requireCorrectiveActionOnFail')
    ) {
      updates.violationMappings = field.violationMappings.map((mapping, index) => {
        if (index !== 0) return mapping;
        if (key === 'requireNotesOnFail') return { ...mapping, requireNotes: value };
        if (key === 'requirePhotosOnFail') return { ...mapping, requirePhotos: value };
        if (key === 'requireLocationOnFail') return { ...mapping, requireLocation: value };
        return { ...mapping, requireCorrectiveAction: value };
      });
    }
    onUpdate(field.id, updates);
  };
  const addOption = () => {
    const next = [...options, { value: `option${options.length + 1}`, label: `Option ${options.length + 1}` }];
    onUpdate(field.id, { extras: { ...field.extras, options: next } });
  };
  const removeOption = (idx: number) => {
    onUpdate(field.id, { extras: { ...field.extras, options: options.filter((_, i) => i !== idx) } });
  };

  return (
    <div className="flex-1 overflow-y-auto divide-y divide-gray-100">

      {/* Identity */}
      <AccordionGroup
        title="Identity"
        defaultOpen
        badge={<CompletionBadge complete={basicComplete} />}
      >
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
          <input
            type="text"
            value={field.label}
            title="Field label"
            placeholder="Field label…"
            onChange={(e) => onUpdate(field.id, { label: e.target.value })}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary) focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Help Text</label>
          <input
            type="text"
            value={field.helpText || ''}
            onChange={(e) => onUpdate(field.id, { helpText: e.target.value || undefined })}
            placeholder="Optional help text…"
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary) focus:border-transparent"
          />
        </div>
      </AccordionGroup>

      {/* Display */}
      <AccordionGroup title="Display" defaultOpen={false}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-500">Type:</span>
          <span className="text-xs font-medium text-gray-700 capitalize">{field.type}</span>
        </div>
        {/* Column Span */}
        {(() => {
          const fieldSection = sections.find((s) => s.fieldIds.includes(field.id));
          if (!fieldSection || (fieldSection.columns || 1) <= 1) return null;
          const maxSpan = fieldSection.columns;
          const currentSpan = field.extras?.columnSpan || 1;
          return (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Column Span</label>
              <div className="flex items-center gap-1">
                {Array.from({ length: maxSpan }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => onUpdate(field.id, { extras: { ...field.extras, columnSpan: n === 1 ? undefined : n } })}
                    className={`px-2.5 py-1 rounded-sm text-xs font-medium transition-colors ${currentSpan === n ? 'bg-(--brand-primary) text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {n === maxSpan ? `${n} (full)` : n}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={field.initialVisibility === 'hidden'} onChange={(e) => onUpdate(field.id, { initialVisibility: e.target.checked ? 'hidden' : 'shown' })} className="rounded-sm border-gray-300" />
          <span className="text-sm text-gray-700">Load Hidden</span>
        </label>
      </AccordionGroup>

      {/* Report output */}
      <AccordionGroup
        title="Report output"
        defaultOpen={false}
        badge={field.extras?.alwaysOnReport ? <FileText size={10} className="text-(--brand-primary)" /> : undefined}
      >
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            aria-label="Always include on report"
            checked={!!field.extras?.alwaysOnReport}
            onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, alwaysOnReport: e.target.checked || undefined } })}
            className="mt-0.5 rounded-sm border-gray-300"
          />
          <div className="flex-1">
            <span className="text-sm text-gray-700">Always include on report</span>
            <p className="text-[10px] text-gray-400">
              This field is printed on the generated report <strong>every time</strong>, even when the
              question passes and has no violation. Leave this off to only show the field through the
              violation / pass-fail rules.
            </p>
          </div>
        </label>
      </AccordionGroup>

      {/* Validation */}
      <AccordionGroup
        title="Validation"
        defaultOpen={false}
        badge={
          (field.required || field.extras?.minLength || field.extras?.maxLength || field.extras?.pattern || field.extras?.min != null || field.extras?.max != null)
            ? <ShieldCheck size={10} className="text-green-500" />
            : undefined
        }
      >
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={field.required || false} onChange={(e) => onUpdate(field.id, { required: e.target.checked })} className="rounded-sm border-gray-300" />
          <div className="flex-1">
            <span className="text-sm text-gray-700">Required</span>
            <p className="text-[10px] text-gray-400">Inspector must fill this field before submitting.</p>
          </div>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={field.naEnabled || false} onChange={(e) => onUpdate(field.id, { naEnabled: e.target.checked })} className="rounded-sm border-gray-300" />
          <div className="flex-1">
            <span className="text-sm text-gray-700">Allow N/A</span>
            <p className="text-[10px] text-gray-400">Inspector can mark this field as not applicable.</p>
          </div>
        </label>
        {/* Text / Textarea: min/max length + pattern */}
        {(field.type === 'text' || field.type === 'textarea') && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Min Length</label>
                <input
                  type="number" min={0} title="Minimum character length"
                  value={field.extras?.minLength ?? ''}
                  onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, minLength: e.target.value !== '' ? parseInt(e.target.value) : undefined } })}
                  placeholder="—"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-blue-300"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Max Length</label>
                <input
                  type="number" min={1} title="Maximum character length"
                  value={field.extras?.maxLength ?? ''}
                  onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxLength: e.target.value !== '' ? parseInt(e.target.value) : undefined } })}
                  placeholder="—"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-blue-300"
                />
              </div>
            </div>
            {field.type === 'text' && (
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Pattern (Regex)</label>
                <input
                  type="text" title="Validation regex pattern"
                  value={field.extras?.pattern ?? ''}
                  onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, pattern: e.target.value || undefined } })}
                  placeholder="e.g. ^[A-Z]{2}\\d{4}$"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm font-mono focus:outline-hidden focus:ring-1 focus:ring-blue-300"
                />
                <p className="text-[9px] text-gray-400 mt-0.5">JavaScript regex — value must match to pass.</p>
              </div>
            )}
          </>
        )}
        {/* Number / Measurement: show min/max as validation context (edited in Field Settings) */}
        {(field.type === 'number' || field.type === 'measurement') && (field.extras?.min != null || field.extras?.max != null) && (
          <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-sm text-[10px] text-gray-500">
            <Info size={9} className="text-gray-400 shrink-0" />
            Range: {field.extras?.min != null ? field.extras.min : '—'} – {field.extras?.max != null ? field.extras.max : '—'}
            {field.extras?.unit ? ` ${field.extras.unit}` : ''}
            <span className="text-gray-400 ml-1">(edit in Field Settings)</span>
          </div>
        )}
      </AccordionGroup>

      {/*
        Behavior — Section 2.7.3 audit: per-type runtime behavior controls.
        Pass/Fail scoring lives here for eligible types; when enabled, the
        ViolationConfigSection and "When marked failed" additional-capture
        toggles are folded inline (2.7.4) — there is NO separate Pass/Fail
        or Additional Capture accordion anymore.
      */}
      <AccordionGroup title="Behavior" defaultOpen={false}>
        {/* ── Text ── */}
        {field.type === 'text' && (
          <>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Auto-capitalize</label>
              <select
                value={field.extras?.autoCapitalize || 'none'}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, autoCapitalize: e.target.value === 'none' ? undefined : e.target.value } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
              >
                <option value="none">None</option>
                <option value="sentences">Sentences</option>
                <option value="words">Words</option>
                <option value="characters">All caps</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.trimOnBlur || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, trimOnBlur: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Trim whitespace on blur</span>
            </label>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Text transform</label>
              <select
                value={field.extras?.textTransform || 'none'}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, textTransform: e.target.value === 'none' ? undefined : e.target.value } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              >
                <option value="none">None</option>
                <option value="uppercase">UPPERCASE</option>
                <option value="lowercase">lowercase</option>
                <option value="titleCase">Title Case</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Placeholder</label>
              <input
                type="text"
                value={field.extras?.placeholder || ''}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, placeholder: e.target.value || undefined } })}
                placeholder="e.g. Enter name…"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              />
            </div>
          </>
        )}

        {/* ── Textarea ── */}
        {field.type === 'textarea' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.autoGrow || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, autoGrow: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Auto-grow rows</span>
            </label>
            {field.extras?.autoGrow && (
              <div className="grid grid-cols-2 gap-2 ml-6">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Min rows</label>
                  <input type="number" min={1} max={20} value={field.extras?.minRows ?? 3} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, minRows: parseInt(e.target.value) || 3 } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-0.5">Max rows</label>
                  <input type="number" min={1} max={20} value={field.extras?.maxRowsTextarea ?? 10} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxRowsTextarea: parseInt(e.target.value) || 10 } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.spellcheck !== false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, spellcheck: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Spellcheck</span>
            </label>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Placeholder</label>
              <input
                type="text"
                value={field.extras?.placeholder || ''}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, placeholder: e.target.value || undefined } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              />
            </div>
          </>
        )}

        {/* ── Number ── */}
        {field.type === 'number' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Step</label>
                <input type="number" min={0} step="any" value={field.extras?.step ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, step: e.target.value !== '' ? Number(e.target.value) : undefined } })} placeholder="1" className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Decimals</label>
                <input type="number" min={0} max={6} value={field.extras?.decimals ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, decimals: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} placeholder="0" className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.allowNegative !== false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, allowNegative: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Allow negative values</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.showSpinner || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, showSpinner: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Show spinner buttons</span>
            </label>
          </>
        )}

        {/* ── Measurement ── */}
        {field.type === 'measurement' && (
          <>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Default unit</label>
              <select
                value={field.extras?.defaultUnit || ''}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, defaultUnit: e.target.value || undefined } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              >
                <option value="">(none)</option>
                {(field.extras?.unitOptions || ['ft', 'in', 'm', 'cm']).map((u: string) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.allowUnitChange !== false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, allowUnitChange: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Allow inspector to change unit</span>
            </label>
          </>
        )}

        {/* ── Date ── */}
        {field.type === 'date' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.defaultToToday || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, defaultToToday: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Default to today</span>
            </label>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Min date (relative days, negative = past)</label>
              <input type="number" value={field.extras?.minDateOffsetDays ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, minDateOffsetDays: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} placeholder="e.g. 0 = today" className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Max date (relative days)</label>
              <input type="number" value={field.extras?.maxDateOffsetDays ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxDateOffsetDays: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} placeholder="e.g. 30" className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.disableWeekends || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, disableWeekends: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Disable weekends</span>
            </label>
          </>
        )}

        {/* ── DateTime ── */}
        {field.type === 'dateTime' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.defaultToNow || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, defaultToNow: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Default to now</span>
            </label>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Min offset days</label>
              <input type="number" value={field.extras?.minDateOffsetDays ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, minDateOffsetDays: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Max offset days</label>
              <input type="number" value={field.extras?.maxDateOffsetDays ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxDateOffsetDays: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Time zone handling</label>
              <select
                value={field.extras?.timeZoneMode || 'local'}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, timeZoneMode: e.target.value } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              >
                <option value="local">Local</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Step (minutes)</label>
              <select
                value={field.extras?.stepMinutes ?? 5}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, stepMinutes: parseInt(e.target.value) } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={60}>60</option>
              </select>
            </div>
          </>
        )}

        {/* ── Toggle ── */}
        {field.type === 'toggle' && (
          <>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Default state</label>
              <select
                value={field.extras?.defaultState || 'unset'}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, defaultState: e.target.value } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              >
                <option value="unset">Unset</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </div>
          </>
        )}

        {/* ── Dropdown ── */}
        {field.type === 'dropdown' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.allowOther || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, allowOther: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Allow "Other" custom value</span>
            </label>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Sort order</label>
              <select
                value={field.extras?.sortOrder || 'asAuthored'}
                onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, sortOrder: e.target.value } })}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm"
              >
                <option value="asAuthored">As authored</option>
                <option value="alphabetical">Alphabetical</option>
              </select>
            </div>
          </>
        )}

        {/* ── Checkbox group ── */}
        {field.type === 'checkboxGroup' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Min selections</label>
                <input type="number" min={0} value={field.extras?.minSelections ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, minSelections: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Max selections</label>
                <input type="number" min={1} value={field.extras?.maxSelections ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxSelections: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.showCheckAll || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, showCheckAll: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Show "Check all" button</span>
            </label>
          </>
        )}

        {/* ── Radio group ── */}
        {field.type === 'radioGroup' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.allowClear || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, allowClear: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Allow "Clear selection"</span>
            </label>
          </>
        )}

        {/* ── Camera ── */}
        {field.type === 'camera' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.requireGps || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, requireGps: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Require GPS tag</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.requireTimestamp !== false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, requireTimestamp: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Require timestamp</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.allowGallery || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, allowGallery: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Allow gallery pick</span>
            </label>
          </>
        )}

        {/* ── Photo ── */}
        {field.type === 'photo' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.allowGallery !== false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, allowGallery: e.target.checked, cameraOnly: e.target.checked ? false : field.extras?.cameraOnly } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Allow gallery</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.cameraOnly || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, cameraOnly: e.target.checked, allowGallery: e.target.checked ? false : field.extras?.allowGallery } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Camera only (no gallery)</span>
            </label>
          </>
        )}

        {/* ── Signature ── */}
        {field.type === 'signature' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.requireSignerName || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, requireSignerName: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Require signer name</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.includeGpsOnSign || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, includeGpsOnSign: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Include GPS when signed</span>
            </label>
          </>
        )}

        {/* ── Location ── */}
        {field.type === 'location' && (
          <>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={field.extras?.autoCaptureOnOpen || false} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, autoCaptureOnOpen: e.target.checked } })} className="rounded-sm border-gray-300" />
              <span className="text-sm text-gray-700">Auto-capture on form open</span>
            </label>
            <div>
              <label className="block text-[10px] text-gray-500 mb-0.5">Accuracy threshold (meters)</label>
              <input type="number" min={1} max={1000} value={field.extras?.accuracyThresholdM ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, accuracyThresholdM: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} placeholder="e.g. 10" className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
            </div>
          </>
        )}

        {/* ── Repeatable list ── */}
        {field.type === 'repeatableList' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Min rows</label>
                <input type="number" min={0} value={field.extras?.minRowsList ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, minRowsList: e.target.value !== '' ? parseInt(e.target.value) : undefined } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-0.5">Max rows</label>
                <input type="number" min={1} max={200} value={field.extras?.maxRows ?? 50} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxRows: parseInt(e.target.value) || 50 } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
            </div>
          </>
        )}

        {/* ── Instructions (no runtime behavior) ── */}
        {field.type === 'instructions' && (
          <p className="text-[11px] text-gray-500 italic px-1">Instructions fields have no runtime behavior — they only display content.</p>
        )}

        {/* ── Data-bound (behavior lives on the widget definition) ── */}
        {field.type === 'dataBound' && (
          <p className="text-[11px] text-gray-500 italic px-1">Data-bound behavior is defined by the widget — see Data Binding below.</p>
        )}

        {/* ── Pass/Fail scoring (eligible types) ── */}
        {hasPassFailSupport && (
          <div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
            <ViolationConfigSection
              field={field}
              onUpdate={onUpdate}
              dexieDb={dexieDb}
              asyncViolationRanker={asyncViolationRanker}
            />
            {field.extras?.hasPassFail && (
              <div className="ml-4 mt-2 pt-2 border-t border-gray-100 space-y-1.5">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">When marked failed</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.extras?.showNotesAlways || false} onChange={(e) => updateFailureCaptureOption('showNotesAlways', e.target.checked)} className="rounded-sm border-gray-300" />
                  <span className="text-sm text-gray-700">Always show notes field</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.extras?.showCameraAlways || false} onChange={(e) => updateFailureCaptureOption('showCameraAlways', e.target.checked)} className="rounded-sm border-gray-300" />
                  <span className="text-sm text-gray-700">Always show camera</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.extras?.requireNotesOnFail ?? field.violationMappings?.[0]?.requireNotes ?? false} onChange={(e) => updateFailureCaptureOption('requireNotesOnFail', e.target.checked)} className="rounded-sm border-gray-300" />
                  <span className="text-sm text-gray-700">Require notes on fail</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.extras?.requirePhotosOnFail ?? field.violationMappings?.[0]?.requirePhotos ?? false} onChange={(e) => updateFailureCaptureOption('requirePhotosOnFail', e.target.checked)} className="rounded-sm border-gray-300" />
                  <span className="text-sm text-gray-700">Require picture on fail</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.extras?.requireLocationOnFail ?? field.violationMappings?.[0]?.requireLocation ?? false} onChange={(e) => updateFailureCaptureOption('requireLocationOnFail', e.target.checked)} className="rounded-sm border-gray-300" />
                  <span className="text-sm text-gray-700">Require location on fail</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={(field.extras?.requireCorrectiveActionOnFail as boolean | undefined) ?? field.violationMappings?.[0]?.requireCorrectiveAction ?? false} onChange={(e) => updateFailureCaptureOption('requireCorrectiveActionOnFail', e.target.checked)} className="rounded-sm border-gray-300" />
                  <span className="text-sm text-gray-700">Require corrective action on fail</span>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Absolute-minimum fallback (should rarely fire) */}
        {!hasPassFailSupport && !['text','textarea','number','measurement','date','dateTime','toggle','dropdown','checkboxGroup','radioGroup','camera','photo','signature','location','repeatableList','instructions','dataBound','inspectors'].includes(field.type) && (
          <p className="text-[11px] text-gray-400 italic px-1">No behaviors for this field type.</p>
        )}
      </AccordionGroup>

      {/* Options */}
      {hasOptions && (
        <AccordionGroup title="Options" defaultOpen badge={<span className="text-[10px] text-gray-400">{options.length}</span>}>
          <div className="space-y-1">
            {options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <input
                  type="text"
                  value={opt.label}
                  onChange={(e) => updateOption(idx, 'label', e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
                  placeholder="Label"
                />
                <button onClick={() => removeOption(idx)} title="Remove option" className="p-0.5 text-gray-400 hover:text-red-500"><X size={12} /></button>
              </div>
            ))}
          </div>
          <button onClick={addOption} className="mt-1 text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1">
            <Plus size={10} /> Add Option
          </button>
        </AccordionGroup>
      )}

      {/* Type-specific extras */}
      {(field.type === 'number' || field.type === 'measurement' || field.type === 'photo' || field.type === 'camera' || field.type === 'instructions' || field.type === 'repeatableList' || field.type === 'toggle') && (
        <AccordionGroup title="Field Settings" defaultOpen={false}>
          {/* Toggle custom labels */}
          {field.type === 'toggle' && (
            <>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!(field.extras?.onLabel || field.extras?.offLabel)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onUpdate(field.id, { extras: { ...field.extras, onLabel: 'Yes', offLabel: 'No' } });
                    } else {
                      const { onLabel: _a, offLabel: _b, ...rest } = field.extras || {};
                      onUpdate(field.id, { extras: rest });
                    }
                  }}
                  className="rounded-sm border-gray-300"
                />
                <span className="text-sm text-gray-700">Custom Labels</span>
              </label>
              {!!(field.extras?.onLabel || field.extras?.offLabel) && (
                <div className="grid grid-cols-2 gap-2 ml-6">
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">On (True)</label>
                    <input type="text" value={field.extras?.onLabel || ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, onLabel: e.target.value } })} placeholder="Yes" className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-500 mb-0.5">Off (False)</label>
                    <input type="text" value={field.extras?.offLabel || ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, offLabel: e.target.value } })} placeholder="No" className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm" />
                  </div>
                </div>
              )}
            </>
          )}
          {/* Number min/max */}
          {(field.type === 'number' || field.type === 'measurement') && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min</label>
                <input type="number" title="Minimum value" value={field.extras?.min ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, min: e.target.value ? Number(e.target.value) : undefined } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max</label>
                <input type="number" title="Maximum value" value={field.extras?.max ?? ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, max: e.target.value ? Number(e.target.value) : undefined } })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
            </div>
          )}
          {/* Measurement units */}
          {field.type === 'measurement' && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600">Unit Options</label>
              <div className="space-y-1">
                {(field.extras?.unitOptions || ['ft', 'in', 'm', 'cm']).map((unit: string, i: number) => (
                  <div key={i} className="flex items-center gap-1">
                    <input type="text" value={unit} title="Unit label" placeholder="e.g. ft" onChange={(e) => { const units = [...(field.extras?.unitOptions || ['ft', 'in', 'm', 'cm'])]; units[i] = e.target.value; onUpdate(field.id, { extras: { ...field.extras, unitOptions: units } }); }} className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-sm" />
                    <button onClick={() => { const units = (field.extras?.unitOptions || ['ft', 'in', 'm', 'cm']).filter((_: string, idx: number) => idx !== i); onUpdate(field.id, { extras: { ...field.extras, unitOptions: units } }); }} title="Remove unit" className="p-0.5 text-gray-400 hover:text-red-500"><X size={12} /></button>
                  </div>
                ))}
                <button onClick={() => { const units = [...(field.extras?.unitOptions || ['ft', 'in', 'm', 'cm']), '']; onUpdate(field.id, { extras: { ...field.extras, unitOptions: units } }); }} className="text-xs text-(--brand-primary) hover:underline">+ Add Unit</button>
              </div>
            </div>
          )}
          {/* Photo max */}
          {(field.type === 'photo' || field.type === 'camera') && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Photos</label>
              <input type="number" min={1} max={20} title="Max photos" value={field.extras?.maxPhotos ?? 5} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxPhotos: parseInt(e.target.value) || 5 } })} className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-sm" />
            </div>
          )}
          {/* Instructions text */}
          {field.type === 'instructions' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Instruction Content</label>
              <textarea value={field.extras?.instructionText || ''} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, instructionText: e.target.value } })} rows={5} placeholder="Enter instructions for the inspector…" className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-sm resize-y" />
            </div>
          )}
          {/* Repeatable sub-fields */}
          {field.type === 'repeatableList' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sub-Fields</label>
              <div className="space-y-1.5">
                {(field.extras?.subFields || [{ key: 'value', label: 'Value', type: 'text' }]).map((sf: { key: string; label: string; type: string }, idx: number) => (
                  <div key={idx} className="flex items-center gap-1">
                    <input type="text" value={sf.label} title="Column label" onChange={(e) => { const subs = [...(field.extras?.subFields || [])]; subs[idx] = { ...subs[idx], label: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, '_') || `col_${idx}` }; onUpdate(field.id, { extras: { ...field.extras, subFields: subs } }); }} placeholder="Column label" className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-sm" />
                    <select value={sf.type} title="Column type" onChange={(e) => { const subs = [...(field.extras?.subFields || [])]; subs[idx] = { ...subs[idx], type: e.target.value }; onUpdate(field.id, { extras: { ...field.extras, subFields: subs } }); }} className="w-20 px-1 py-1 text-xs border border-gray-300 rounded-sm">
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="toggle">Toggle</option>
                    </select>
                    <button onClick={() => { const subs = (field.extras?.subFields || []).filter((_: any, i: number) => i !== idx); if (subs.length > 0) onUpdate(field.id, { extras: { ...field.extras, subFields: subs } }); }} title="Remove column" className="p-0.5 text-gray-400 hover:text-red-500"><X size={12} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => { const subs = [...(field.extras?.subFields || [])]; const i = subs.length; subs.push({ key: `col_${i}`, label: `Column ${i + 1}`, type: 'text' }); onUpdate(field.id, { extras: { ...field.extras, subFields: subs } }); }} className="mt-1 text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1"><Plus size={10} /> Add Column</button>
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Rows</label>
                <input type="number" min={1} max={200} title="Max rows" value={field.extras?.maxRows ?? 50} onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, maxRows: parseInt(e.target.value) || 50 } })} className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-sm" />
              </div>
            </div>
          )}
        </AccordionGroup>
      )}

      {/* Data Bound config — Section 2.7.5: registry-backed picker for custom dataBound fields */}
      {field.type === 'dataBound' && (() => {
        const widgetDef = field.extras?.widgetId
          ? DATA_BOUND_WIDGET_REGISTRY.find((w) => w.widgetId === field.extras!.widgetId)
          : null;

        // Allowed entity categories for this templateType (for entityCategoryFilter).
        const allowedCategories = templateType
          ? (TEMPLATE_TYPE_TO_ENTITY_CATEGORIES[templateType as TemplateType] ?? null)
          : null;

        // Widget passes the template-type filter if it has no filter OR the
        // filter intersects the allowed categories.
        const matchesTemplateType = (w: DataBoundWidgetDefinition) => {
          if (!w.entityCategoryFilter || w.entityCategoryFilter.length === 0) return true;
          if (!allowedCategories) return true;
          return w.entityCategoryFilter.some((c) => allowedCategories.includes(c));
        };

        const dataSources: DataBoundDataSource[] = Array.from(
          new Set(DATA_BOUND_WIDGET_REGISTRY.map((w) => w.dataSource)),
        );
        const selectedDataSource = field.extras?.dataSource as DataBoundDataSource | undefined;
        const fieldsForSource = selectedDataSource
          ? DATA_BOUND_WIDGET_REGISTRY.filter((w) => w.dataSource === selectedDataSource && matchesTemplateType(w))
          : [];

        // Promote a custom dataBound field to a pre-built widget by copying
        // the full registry entry into field.extras (same shape the palette
        // drag produces via formService.createWidgetField).
        const promoteToWidget = (w: DataBoundWidgetDefinition) => {
          onUpdate(field.id, {
            extras: {
              ...field.extras,
              widgetId: w.widgetId,
              dataSource: w.dataSource,
              boundField: w.boundField,
              displayFormat: w.displayFormat,
              inputType: w.inputType,
              lookupType: w.lookupType,
              defaultMode: w.defaultMode,
              forceReadOnly: w.forceReadOnly,
              // Default mode mirrors the widget's defaultMode; editable only if allowed.
              mode: w.forceReadOnly ? 'readOnly' : (field.extras?.mode || w.defaultMode),
            },
          });
        };

        const clearWidget = () => {
          const { widgetId: _wid, boundField: _bf, displayFormat: _df, inputType: _it, lookupType: _lt, defaultMode: _dm, forceReadOnly: _fro, ...rest } = field.extras || {};
          onUpdate(field.id, { extras: rest });
        };

        const incomplete = !widgetDef && (!selectedDataSource || !fieldsForSource.length);

        return (
          <AccordionGroup
            title="Data Binding"
            defaultOpen
            badge={incomplete ? <AlertTriangle size={10} className="text-amber-500" /> : undefined}
          >
            {/* Widget info banner */}
            {widgetDef ? (
              <div className="flex flex-col gap-1 px-2.5 py-2 bg-orange-50 border border-orange-200 rounded-sm">
                <div className="flex items-center gap-1.5">
                  <Database size={11} className="text-orange-500 shrink-0" />
                  <span className="text-[11px] font-semibold text-orange-800">{widgetDef.label}</span>
                </div>
                {widgetDef.description && (
                  <p className="text-[10px] text-orange-700 leading-relaxed">{widgetDef.description}</p>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[9px] text-orange-500 bg-orange-100 px-1.5 py-0.5 rounded-sm">{widgetDef.widgetCategory}</span>
                  {widgetDef.boundField && (
                    <span className="text-[9px] text-orange-400 font-mono">{widgetDef.boundField}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearWidget}
                  className="mt-1 self-start text-[10px] text-orange-700 hover:text-orange-900 underline"
                  title="Unbind — choose a different data source / field"
                >
                  Change binding
                </button>
              </div>
            ) : (
              <div className="flex items-start gap-1.5 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-800">
                <AlertTriangle size={10} className="text-amber-500 shrink-0 mt-0.5" />
                <span>Select a data source and field to finish configuring this data-bound widget.</span>
              </div>
            )}

            {/* Step 1 — Data Source picker */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data Source</label>
              {widgetDef ? (
                <div className="px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-sm text-gray-500">{widgetDef.dataSource}</div>
              ) : (
                <select
                  value={selectedDataSource || ''}
                  title="Data source"
                  onChange={(e) => onUpdate(field.id, { extras: { ...field.extras, dataSource: e.target.value || undefined, boundField: undefined, widgetId: undefined } })}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm"
                >
                  <option value="">Select data source…</option>
                  {dataSources.map((ds) => (
                    <option key={ds} value={ds}>{ds === 'inspectableEntity' ? 'Inspectable Entity' : ds === 'inspectionEvent' ? 'Inspection Event' : ds === 'cases' ? 'Cases' : ds === 'inspectionCases' ? 'Inspection Cases' : ds}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Step 2 — Field picker (appears once data source selected) */}
            {!widgetDef && selectedDataSource && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Field</label>
                {fieldsForSource.length === 0 ? (
                  <div className="px-2 py-1.5 text-[11px] bg-gray-50 border border-gray-200 rounded-sm text-gray-500">
                    No pre-built widgets for this data source match the template type{templateType ? ` (${templateType})` : ''}.
                  </div>
                ) : (
                  <select
                    value=""
                    title="Bound field"
                    onChange={(e) => {
                      const widget = fieldsForSource.find((w) => w.widgetId === e.target.value);
                      if (widget) promoteToWidget(widget);
                    }}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm"
                  >
                    <option value="">Select a field…</option>
                    {fieldsForSource.map((w) => (
                      <option key={w.widgetId} value={w.widgetId}>
                        {w.label}{w.boundField ? ` — ${w.boundField}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-[10px] text-gray-400 mt-1 leading-relaxed">
                  Picking a field promotes this widget — it will render with full lookup support.
                </p>
              </div>
            )}

            {/* Mode toggle (readOnly / editable) */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mode</label>
              {field.extras?.forceReadOnly ? (
                <div className="px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-sm text-gray-500">Read Only (locked)</div>
              ) : (
                <div className="flex gap-1">
                  {(['readOnly', 'editable'] as const).map((m) => (
                    <button
                      key={m}
                      onClick={() => onUpdate(field.id, { extras: { ...field.extras, mode: m } })}
                      className={`flex-1 px-2 py-1.5 text-xs rounded-sm border transition-colors ${(field.extras?.mode || 'readOnly') === m ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                    >
                      {m === 'readOnly' ? 'Read Only' : 'Editable'}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {widgetDef?.description && (
              <div className="flex items-start gap-1.5 mt-1 px-2 py-1.5 bg-blue-50 border border-blue-100 rounded-sm">
                <Info size={10} className="text-blue-400 shrink-0 mt-0.5" />
                <p className="text-[10px] text-blue-600 leading-relaxed">
                  This field auto-populates from <strong>{widgetDef.dataSource === 'inspectableEntity' ? 'the inspected entity' : widgetDef.dataSource === 'inspectionEvent' ? 'the inspection event' : 'the inspection case'}</strong>. {widgetDef.defaultMode === 'readOnly' ? 'It is read-only by default.' : 'It can be edited by inspectors.'}
                </p>
              </div>
            )}
          </AccordionGroup>
        );
      })()}

      {/* Visibility Rules */}
      {hasVisibilityRuleSupport && (
        <AccordionGroup
          title="Visibility Rules"
          defaultOpen={fieldRules.length > 0}
          badge={fieldRules.length > 0 ? <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">{fieldRules.length}</span> : undefined}
        >
          <VisibilityRulesSection
            field={field}
            sections={sections}
            allFields={allFields}
            rules={rules}
            onAddRule={onAddRule}
            onUpdateRule={onUpdateRule}
            onRemoveRule={onRemoveRule}
          />
        </AccordionGroup>
      )}

      {/*
        Section 2.7.4: Pass / Fail & Violations and Additional Capture are
        NO LONGER standalone accordions — both are folded inline inside
        Behavior when Pass/Fail is enabled, under a "When marked failed"
        subheading. This prevents the confusing split across accordions
        and eliminates the empty-looking Behavior group.
      */}

      {/* Delete */}
      <div className="px-3 py-3">
        <button
          onClick={() => onRemove(field.id)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
        >
          <Trash2 size={14} /> Remove Field
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PropertiesPanel — main exported component
// ---------------------------------------------------------------------------

export function PropertiesPanel({
  selection,
  field,
  section,
  form,
  sections,
  allFields,
  rules,
  onUpdateField,
  onUpdateSection,
  onUpdateForm,
  onDeleteField,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  onClose,
  pinned,
  onPin,
  templateName,
  templateType,
  templateStatus,
  panelWidth,
  categoryPool,
  dexieDb,
  asyncViolationRanker,
}: PropertiesPanelProps) {
  const headerContent = (() => {
    if (!selection) return { icon: <Settings size={13} className="text-gray-400" />, label: 'Properties' };
    if (selection.level === 'field' && field) {
      const Icon = FIELD_ICONS[field.type];
      return { icon: <Icon size={13} className="text-gray-400" />, label: field.label || 'Field' };
    }
    if (selection.level === 'section' && section) {
      return { icon: <Columns size={13} className="text-gray-400" />, label: section.title || 'Section' };
    }
    if (selection.level === 'form' && form) {
      return { icon: <LayoutDashboard size={13} className="text-gray-400" />, label: form.name || 'Form' };
    }
    return { icon: <Settings size={13} className="text-gray-400" />, label: 'Properties' };
  })();

  return (
    <div
      className="h-full border-l bg-white flex flex-col overflow-hidden"
      /* eslint-disable-next-line react/forbid-component-props */
      style={panelWidth ? { width: panelWidth } : undefined}
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b bg-gray-50 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {headerContent.icon}
          <h3 className="text-xs font-semibold text-gray-600 truncate">{headerContent.label}</h3>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {onPin && (
            <button onClick={onPin} title={pinned ? 'Unpin panel' : 'Pin panel open'} className="p-0.5 text-gray-400 hover:text-gray-600 rounded-sm">
              {pinned ? <PinOff size={12} /> : <Pin size={12} />}
            </button>
          )}
          {(!pinned || selection) && (
            <button onClick={onClose} title="Close" className="p-0.5 text-gray-400 hover:text-gray-600 rounded-sm">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Empty state — show template summary when pinned and nothing selected */}
      {!selection && (
        <div className="flex-1 overflow-y-auto">
          {templateName ? (
            <div className="p-3 space-y-2">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Template</p>
              <p className="text-sm font-medium text-gray-800 wrap-break-word">{templateName}</p>
              {templateType && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500">Type:</span>
                  <span className="text-[10px] font-medium text-gray-700">{templateType}</span>
                </div>
              )}
              {templateStatus && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500">Status:</span>
                  <span className={`text-[10px] font-medium capitalize ${
                    templateStatus === 'published' ? 'text-green-600' : 'text-amber-600'
                  }`}>{templateStatus}</span>
                </div>
              )}
              <p className="text-[10px] text-gray-400 pt-2">Select a field or section to edit its properties.</p>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4">
              <p className="text-xs text-gray-400 text-center">Select a field or section to edit its properties.</p>
            </div>
          )}
        </div>
      )}

      {/* Field properties */}
      {selection?.level === 'field' && field && (
        <FieldPropertiesPanel
          field={field}
          sections={sections}
          allFields={allFields}
          rules={rules}
          onUpdate={onUpdateField}
          onRemove={onDeleteField}
          onAddRule={onAddRule}
          onUpdateRule={onUpdateRule}
          onRemoveRule={onRemoveRule}
          dexieDb={dexieDb}
          asyncViolationRanker={asyncViolationRanker}
          templateType={templateType}
        />
      )}

      {/* Section properties */}
      {selection?.level === 'section' && section && (
        <SectionPropertiesPanel section={section} onUpdate={onUpdateSection} />
      )}

      {/* Form properties */}
      {selection?.level === 'form' && form && onUpdateForm && (
        <FormPropertiesPanel form={form} onUpdate={onUpdateForm} categoryPool={categoryPool} />
      )}
      {selection?.level === 'form' && (!form || !onUpdateForm) && (
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-xs text-gray-400 text-center">Select a form block to view its properties.</p>
        </div>
      )}
    </div>
  );
}

export default PropertiesPanel;
